import React from "react";
import {
  Toolbar, Typography, Box, TextField, MenuItem, Button,
  FormControl, InputLabel, Select, InputAdornment
} from "@mui/material";
import { Search, FilterList } from "@mui/icons-material";
import { DriverStatus } from "../../types/enums";
import PageHelp from "../../components/common/PageHelp";
import AppVersionFilter from "../../components/AppVersionFilter";
import { AppVersionOptions } from "../../hooks/useAppVersionOptions";

interface Props {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  availabilityFilter: string;
  setAvailabilityFilter: (v: string) => void;
  approvalFilter: string;
  setApprovalFilter: (v: string) => void;
  appVersionFilter: string;
  setAppVersionFilter: (v: string) => void;
  appVersionOptions: AppVersionOptions;
}

/** Search box + status/availability/approval filters for the driver list. */
const DriverFiltersToolbar: React.FC<Props> = ({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  availabilityFilter, setAvailabilityFilter,
  approvalFilter, setApprovalFilter,
  appVersionFilter, setAppVersionFilter, appVersionOptions,
}) => {
  const hasFilters = !!searchTerm || statusFilter !== 'all' || availabilityFilter !== 'all' || approvalFilter !== 'all' || appVersionFilter !== 'all';
  return (
      <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6">Driver Management</Typography>
          <PageHelp k="drivers" />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
              placeholder="Search by phone, name, plate, license…"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value={DriverStatus.ONLINE}>Online</MenuItem>
              <MenuItem value={DriverStatus.OFFLINE}>Offline</MenuItem>
              <MenuItem value={DriverStatus.SILENT}>Silent</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Availability</InputLabel>
            <Select
                value={availabilityFilter}
                label="Availability"
                onChange={(e) => setAvailabilityFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="unavailable">Unavailable</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Approval</InputLabel>
            <Select
                value={approvalFilter}
                label="Approval"
                onChange={(e) => setApprovalFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="ready">✅ Ready to approve</MenuItem>
              <MenuItem value="approved">Approved (verified)</MenuItem>
              <MenuItem value="incomplete">Incomplete docs</MenuItem>
            </Select>
          </FormControl>
          <AppVersionFilter
              value={appVersionFilter}
              onChange={setAppVersionFilter}
              options={appVersionOptions}
          />
          {hasFilters && (
              <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setAvailabilityFilter("all");
                    setApprovalFilter("all");
                    setAppVersionFilter("all");
                  }}
              >
                Clear
              </Button>
          )}
        </Box>
      </Toolbar>
  );
};

export default DriverFiltersToolbar;
